// ContactEditFreeformPanel — the freeform-create branch AND the view-mode
// branch of ContactEditModal.tsx's left-column ternary, extracted verbatim.
// Internally branches on `freeformMode` exactly as the original
// `freeformMode ? (...) : (viewOnly || isViewMode) ? (...) : <form>` did — the
// orchestrator mounts this for BOTH the freeform and view arms (the edit-form
// arm renders ContactEditFormBody instead).
//
// Pure presentational leaf: no state/effects of its own. Markup/handlers/inline
// styles/a11y are byte-identical to the inline original. No `fetch` here.
import React from 'react';
import type { ShopifyOrderSummary, ContactObject } from '../lib/types';
import type { ContactElement } from '@/lib/api/endpoints/objects.transform';
import { ProvenanceIcons } from '../../components/ProvenanceIcons';

export interface ContactEditFreeformPanelProps {
  freeformMode: boolean | undefined;
  isMobile: boolean;
  // freeform state
  freeformText: string;
  handleFreeformTextChange: (text: string) => void;
  gmailResults: Array<{ email: string; name: string; photoUrl?: string | null; score: number }>;
  setGmailResults: (r: any[]) => void;
  handleSelectGmailPerson: (person: { email: string; name: string }) => void;
  handleFreeformFind: () => void;
  freeformParsing: boolean;
  freeformParsed: Record<string, any> | null;
  setFreeformParsed: (v: any) => void;
  // shared field state
  firstName: string;
  setFirstName: (s: string) => void;
  lastName: string;
  setLastName: (s: string) => void;
  name: string;
  setName: (s: string) => void;
  email: string;
  setEmail: (s: string) => void;
  phone: string;
  setPhone: (s: string) => void;
  company: string;
  setCompany: (s: string) => void;
  firm: string;
  setFirm: (s: string) => void;
  contactTitle: string;
  setContactTitle: (s: string) => void;
  location: string;
  setLocation: (s: string) => void;
  linkedinUrl: string;
  setLinkedinUrl: (s: string) => void;
  xHandle: string;
  setXHandle: (s: string) => void;
  summary: string;
  setSummary: (s: string) => void;
  stage: string;
  setFreeformMode: (b: boolean) => void;
  setIsViewMode: (b: boolean) => void;
  // enrichment state
  enrichmentSummary: string;
  setEnrichmentSummary: (s: string) => void;
  enrichmentSkills: string[];
  setEnrichmentSkills: (s: string[]) => void;
  enrichmentConfidence: string;
  setEnrichmentConfidence: (s: string) => void;
  enrichmentAttemptedAt: string;
  setEnrichmentAttemptedAt: (s: string) => void;
  enrichmentTags: string[];
  enrichLoading: boolean;
  enrichUnsaved: boolean;
  setEnrichUnsaved: (b: boolean) => void;
  enrichHintsPrompt: boolean;
  setEnrichHintsPrompt: (b: boolean) => void;
  enrichHints: string;
  setEnrichHints: (s: string) => void;
  setEnrichResult: (v: any) => void;
  setEnrichmentReviewedAtLocal: (s: string) => void;
  enrichmentReviewedAtLocal: string;
  handleEnrich: (extraHintsArg?: string | unknown) => void;
  handleSaveEnrichment: () => void;
  // view-mode props
  contact: ContactObject | null | undefined;
  isNew: boolean | undefined;
  label: string;
  labelColor: string;
  showDealValue: boolean;
  showProbability: boolean;
  dealValue: string;
  probability: string;
  setShowMobileActivity: (b: boolean) => void;
  onSave: (data: Partial<ContactObject>) => void;
}

export function ContactEditFreeformPanel(props: ContactEditFreeformPanelProps) {
  const {
    freeformMode, isMobile, freeformText, handleFreeformTextChange, gmailResults, setGmailResults,
    handleSelectGmailPerson, handleFreeformFind, freeformParsing, freeformParsed, setFreeformParsed,
    firstName, setFirstName, lastName, setLastName, name, setName, email, setEmail, phone, setPhone,
    company, setCompany, firm, setFirm, contactTitle, setContactTitle, location, setLocation,
    linkedinUrl, setLinkedinUrl, xHandle, setXHandle, summary, setSummary, stage,
    setFreeformMode, setIsViewMode, enrichmentSummary, setEnrichmentSummary, enrichmentSkills,
    setEnrichmentSkills, enrichmentConfidence, setEnrichmentConfidence, enrichmentAttemptedAt,
    setEnrichmentAttemptedAt, enrichmentTags, enrichLoading, enrichUnsaved, setEnrichUnsaved,
    enrichHintsPrompt, setEnrichHintsPrompt, enrichHints, setEnrichHints, setEnrichResult,
    setEnrichmentReviewedAtLocal, enrichmentReviewedAtLocal, handleEnrich, handleSaveEnrichment,
    contact, isNew, label, labelColor, showDealValue, showProbability, dealValue, probability,
    setShowMobileActivity, onSave,
  } = props;

  if (freeformMode) {
    return (
      <div style={{ padding: '24px 28px', flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
        {/* Textarea + Find/Enter buttons — hidden once a person is selected or AI results are in */}
        {!enrichmentSummary && !freeformParsed && (
          <>
            <div style={{ position: 'relative' }}>
              <textarea
                value={freeformText}
                onChange={(e) => handleFreeformTextChange(e.target.value)}
                placeholder={"Type a name to search Gmail, or paste anything you know...\n\ne.g. Neda, or Armand Tatevossian 310 865 3091"}
                style={{
                  width: '100%', minHeight: 140, padding: '14px 16px',
                  border: '1px solid #DADCE0', borderRadius: 8,
                  fontSize: 14, fontFamily: 'inherit', color: 'var(--color-text)',
                  resize: 'none', outline: 'none', lineHeight: 1.6,
                  background: '#F8F9FA', boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'var(--color-brand)'; }}
                onBlur={(e) => { setTimeout(() => { e.target.style.borderColor = '#DADCE0'; setGmailResults([]); }, 200); }}
                autoFocus
              />
            </div>
            {/* Gmail people search results — inline below textarea */}
            {gmailResults.length > 0 && (
              <div style={{
                background: 'var(--color-surface)', border: '1px solid #DADCE0', borderRadius: 8,
                boxShadow: 'var(--popover-shadow, 0 4px 12px rgba(0,0,0,0.1))', marginTop: 8, overflow: 'hidden',
              }}>
                <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500, borderBottom: '1px solid #F1F3F4' }}>
                  Gmail contacts
                </div>
                  {gmailResults.map((person, i) => (
                    <div
                      key={i}
                      onMouseDown={() => handleSelectGmailPerson(person)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        cursor: 'pointer', fontSize: 13, borderBottom: i < gmailResults.length - 1 ? '1px solid #f0f0f0' : 'none',
                        borderLeft: '3px solid transparent', transition: 'background 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#EBF5FF'; e.currentTarget.style.borderLeftColor = 'var(--color-brand)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderLeftColor = 'transparent'; }}
                    >
                      {person.photoUrl ? (
                        <img src={person.photoUrl} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} referrerPolicy="no-referrer" />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#DADCE0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#4a4d51' }}>
                          {(person.name || person.email).charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {person.name && <div style={{ fontWeight: 600, color: 'var(--color-text)' }}>{person.name}</div>}
                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{person.email}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                        {person.score >= 10 && (
                          <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>frequent</span>
                        )}
                        <span style={{ fontSize: 11, color: 'var(--color-brand)', fontWeight: 500 }}>Select</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
              <button
                type="button"
                onClick={handleFreeformFind}
                disabled={freeformParsing || !freeformText.trim()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 20px', borderRadius: 8,
                  background: 'var(--color-brand)', color: 'white', border: 'none',
                  fontSize: 14, fontWeight: 600, cursor: freeformParsing ? 'default' : 'pointer',
                  opacity: (freeformParsing || !freeformText.trim()) ? 0.6 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {freeformParsing ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <circle cx="12" cy="12" r="10" opacity="0.25" /><path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                )}
                {freeformParsing ? 'Finding...' : 'Find with AI'}
              </button>
              <button
                type="button"
                onClick={() => {
                  // Carry whatever the user typed into the manual fields
                  // before switching modes — name, email, phone parsed
                  // out heuristically; anything left becomes the name.
                  const raw = freeformText.trim();
                  if (raw) {
                    let leftover = raw;
                    const emailMatch = leftover.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
                    if (emailMatch && !email.trim()) {
                      setEmail(emailMatch[0]);
                      leftover = leftover.replace(emailMatch[0], ' ');
                    }
                    // 10+ digits, optionally with separators or +country
                    const phoneMatch = leftover.match(/(\+?\d[\d\s().-]{8,}\d)/);
                    if (phoneMatch && !phone.trim()) {
                      setPhone(phoneMatch[0].trim());
                      leftover = leftover.replace(phoneMatch[0], ' ');
                    }
                    // Strip linkedin/twitter URLs/handles into their fields
                    const linkedinMatch = leftover.match(/(https?:\/\/)?(www\.)?linkedin\.com\/in\/[\w-]+\/?/i);
                    if (linkedinMatch && !linkedinUrl.trim()) {
                      const url = linkedinMatch[0].startsWith('http') ? linkedinMatch[0] : `https://${linkedinMatch[0]}`;
                      setLinkedinUrl(url);
                      leftover = leftover.replace(linkedinMatch[0], ' ');
                    }
                    const handleMatch = leftover.match(/(?:^|\s)@([\w]{2,15})\b/);
                    if (handleMatch && !xHandle.trim()) {
                      setXHandle(handleMatch[1]);
                      leftover = leftover.replace(handleMatch[0], ' ');
                    }
                    // Whatever remains: words → name
                    const nameTokens = leftover
                      .split(/[\s,;|]+/)
                      .filter(t => t && !/^[\d().+-]+$/.test(t));
                    if (nameTokens.length > 0) {
                      if (!firstName.trim()) setFirstName(nameTokens[0]);
                      if (!lastName.trim() && nameTokens.length > 1) setLastName(nameTokens.slice(1).join(' '));
                      const fullName = nameTokens.join(' ');
                      if (!name.trim()) setName(fullName);
                    }
                  }
                  setFreeformMode(false);
                  setIsViewMode(false);
                }}
                style={{
                  padding: '10px 16px', borderRadius: 8,
                  background: 'none', color: '#4a4d51', border: '1px solid #DADCE0',
                  fontSize: 13, fontWeight: 500, cursor: 'pointer',
                }}
              >
                Enter manually
              </button>
            </div>

            {/* Save button fallback — when parsed but no enrichment yet */}
            {freeformParsed && firstName.trim() && !freeformParsing && !enrichLoading && (
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    const saveBtn = document.querySelector<HTMLButtonElement>('.contact-save-btn');
                    if (saveBtn) saveBtn.click();
                  }}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: 8,
                    background: 'var(--color-brand)', color: 'white', border: 'none',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Save Contact
                </button>
              </div>
            )}
          </>
        )}

        {/* Progressive contact card — shows immediately with what we know */}
        {freeformParsed && firstName.trim() && (
          <>
            {/* Contact details as they arrive */}
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', rowGap: 6, columnGap: 12, fontSize: 13, marginBottom: 12 }}>
              {email && <><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Email</span><span style={{ color: 'var(--color-text)' }}>{email}</span></>}
              {phone && <><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Phone</span><span style={{ color: 'var(--color-text)' }}>{phone}</span></>}
              {contactTitle && <><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Title</span><span style={{ color: 'var(--color-text)' }}>{contactTitle}</span></>}
              {(firm || company) && <><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Company</span><span style={{ color: 'var(--color-text)' }}>{firm || company}</span></>}
              {location && <><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Location</span><span style={{ color: 'var(--color-text)' }}>{location}</span></>}
              {linkedinUrl && <><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>LinkedIn</span><a href={linkedinUrl.startsWith('http') ? linkedinUrl : `https://${linkedinUrl}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b6fc0', textDecoration: 'none' }}>{linkedinUrl.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//, '')}</a></>}
              {xHandle && <><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>X</span><a href={`https://x.com/${xHandle}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b6fc0', textDecoration: 'none' }}>@{xHandle}</a></>}
            </div>

            {/* Profile — appears when enrichment completes. Header
                label kept consistent with the saved-contact view, which
                was unified into a single "Profile" section. */}
            {enrichmentSummary ? (
              <div style={{ padding: '14px 16px', background: '#faf6ee', borderRadius: 8, border: '1px solid #ece6d6' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>
                  Profile
                </div>
                <p style={{ fontFamily: 'inherit', fontSize: 14, lineHeight: 1.6, color: 'var(--color-text)', margin: 0 }}>
                  {enrichmentSummary}
                </p>
              </div>
            ) : enrichLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', background: '#faf6ee', borderRadius: 8, border: '1px solid #ece6d6' }}>
                <div style={{ width: 16, height: 16, border: '2px solid #E8E9EC', borderTopColor: 'var(--color-brand)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Building AI profile...</span>
              </div>
            ) : null}

            {/* Save / Wrong Person */}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={() => {
                  const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
                  if (!fullName) return;
                  const attrs: any = {};
                  if (xHandle.trim()) attrs.xHandle = xHandle.trim();
                  if (linkedinUrl.trim()) attrs.linkedinUrl = linkedinUrl.trim();
                  if (contactTitle.trim()) attrs.jobTitle = contactTitle.trim();
                  if (firm.trim()) attrs.organization = firm.trim();
                  if (location.trim()) attrs.location = location.trim();
                  if (enrichmentSummary) attrs.enrichmentSummary = enrichmentSummary;
                  if (enrichmentSkills.length) attrs.enrichmentSkills = enrichmentSkills;
                  if (enrichmentConfidence) attrs.enrichmentConfidence = enrichmentConfidence;
                  if (enrichmentAttemptedAt) attrs.enrichmentAttemptedAt = enrichmentAttemptedAt;
                  const savePayload = {
                    name: fullName,
                    emails: email.trim() ? [email.trim()] : [],
                    phones: phone.trim() ? [phone.trim()] : [],
                    organization: (company || firm).trim() || undefined,
                    summary: summary.trim() || undefined,
                    stage: stage || 'lead',
                    enrichmentSummary: enrichmentSummary || undefined,
                    enrichmentSkills: enrichmentSkills.length ? enrichmentSkills : undefined,
                    enrichmentConfidence: enrichmentConfidence || undefined,
                    enrichmentAttemptedAt: enrichmentAttemptedAt || undefined,
                    contactTitle: contactTitle.trim() || undefined,
                    linkedinUrl: linkedinUrl.trim() || undefined,
                    location: location.trim() || undefined,
                    firm: (firm || company).trim() || undefined,
                    attributes: Object.keys(attrs).length > 0 ? attrs : undefined,
                    _forceCreate: true,
                  };
                  // Route through the parent's onSave so all the
                  // create-flow logic runs: kanban-column-aware stage
                  // (newContactStage wins), email-based duplicate
                  // detection, AI hint construction, etc. Previously
                  // this button POSTed directly to /contacts and wrote
                  // stage into `attributes.stage` instead of the
                  // top-level field, causing contacts to land in the
                  // default column regardless of which "+" the user
                  // clicked on the kanban.
                  onSave(savePayload as Partial<ContactObject>);
                }}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: 8,
                    background: 'var(--color-brand)', color: 'white', border: 'none',
                    fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Save Contact
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEnrichmentSummary('');
                    setEnrichmentSkills([]);
                    setEnrichmentConfidence('');
                    setEnrichResult(null);
                    setEnrichUnsaved(false);
                    setEnrichHintsPrompt(false);
                    setEnrichHints('');
                    setCompany('');
                    setContactTitle('');
                    setLocation('');
                    setLinkedinUrl('');
                    setSummary('');
                    setFirm('');
                    setFreeformParsed(null);
                    setFirstName('');
                    setLastName('');
                    setName('');
                  }}
                  style={{
                    flex: 1, padding: '10px 16px', borderRadius: 8,
                    background: 'var(--color-surface)', color: '#dc2626', border: '1px solid #fecaca',
                    fontSize: 14, fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  Wrong Person
                </button>
              </div>
          </>
        )}

        {/* Skills from enrichment */}
        {enrichmentSkills.length > 0 && (
          <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {enrichmentSkills.map((skill, i) => (
              <span key={i} style={{ fontSize: 12, padding: '3px 10px', borderRadius: 14, background: 'var(--xavior-blue-bg, #e0f2fe)', color: 'var(--color-brand)', fontWeight: 500 }}>{skill}</span>
            ))}
          </div>
        )}
      </div>
    );
  }

  /* ---- VIEW MODE ---- */
  return (
    <div style={{ padding: '16px 20px 24px 20px' }}>

      {/* Contact Details Grid */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Contact Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', rowGap: 8, columnGap: 12, fontSize: 13 }}>
          {phone && <>
            <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Phone</span>
            <span style={{ color: 'var(--color-text)' }}>{phone}</span>
          </>}
          {(() => {
            // Unified rich entries (value + per-source provenance) are the
            // single source of truth (§3.5); fall back to the derived
            // string array for a contact loaded before the normalizer ran.
            const entries: ContactElement[] = (contact?.emailEntries && contact.emailEntries.length > 0)
              ? contact.emailEntries
              : (Array.isArray(contact?.emails) ? contact.emails : []).map((value: string) => ({ value }));
            if (entries.length === 0) return null;
            return <>
              <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Email</span>
              <span style={{ color: 'var(--color-text)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {entries.map((entry) => (
                  <span key={entry.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span>{entry.value}</span>
                    {/* Per-element provenance (§3.5): which account(s) supplied this address. */}
                    <ProvenanceIcons sources={entry.sources ?? []} />
                  </span>
                ))}
              </span>
            </>;
          })()}
          {/* Title and Firm are already rendered in the subtitle line
              under the contact name — omitted here to avoid showing
              "Founder/CEO" and the company twice. */}
          {(() => {
            const attrs = contact?.attributes || {};
            const city = contact?.city || attrs.city;
            const st = contact?.state || attrs.state;
            const country = contact?.country || attrs.country;
            const loc = [city, st, country].filter(Boolean).join(', ') || location;
            if (!loc) return null;
            return <>
              <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Location</span>
              <span style={{ color: 'var(--color-text)' }}>{loc}</span>
            </>;
          })()}
          {linkedinUrl && <>
            <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>LinkedIn</span>
            <a href={linkedinUrl.startsWith('http') ? linkedinUrl : `https://${linkedinUrl}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b6fc0', textDecoration: 'none' }}>
              {linkedinUrl.replace(/https?:\/\/(www\.)?linkedin\.com\/in\//, '')}
            </a>
          </>}
          {xHandle && <>
            <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>X</span>
            <a href={`https://x.com/${xHandle}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b6fc0', textDecoration: 'none' }}>@{xHandle}</a>
          </>}
        </div>
      </div>

      {/* More — surfaces custom attributes (birthday, facebookId, …)
          written by the agent on user assertions.
          The contact list/detail endpoints flatten attributes to
          top-level, so we merge both shapes here. */}
      {(() => {
        const attrsNested = (contact?.attributes ?? {}) as Record<string, unknown>;
        const attrsFlat = (contact ?? {}) as Record<string, unknown>;
        const attrs: Record<string, unknown> = { ...attrsFlat, ...attrsNested };
        const HIDDEN = new Set([
          'id', 'uuid', 'type', 'name', 'title', 'email', 'emails', 'phone', 'phones',
          // Rich contact element arrays (frontend-computed; emails/phones already listed above)
          'emailEntries', 'phoneEntries',
          // Per-field sync provenance — internal plumbing, never user-facing
          'fieldProvenance', 'field_provenance',
          'firstName', 'lastName', 'what',
          // Job title — shown in the subtitle line; kept out of the More grid to avoid duplication
          'jobTitle', 'job_title',
          'displayName', 'display_name',
          'company', 'organization', 'firm', 'contactTitle', 'location',
          'city', 'state', 'country', 'address', 'zip',
          'linkedinUrl', 'xHandle', 'x_handle', 'role', 'summary',
          'enrichmentSummary', 'enrichmentSkills', 'enrichmentConfidence',
          'enrichmentAttemptedAt', 'enrichmentReviewedAt', 'enrichmentTags',
          'doNotBulkEnrich', 'enrichHints',
          'dealValue', 'probability', 'activities', 'notes',
          'label', 'labelColor', 'nextStep', 'stage', 'order',
          'authorName', 'authorId',
          'createdAt', 'updatedAt', 'created_at', 'updated_at',
          'archived_at', 'archivedAt', 'completed', 'completed_at', 'completedAt',
          'status', 'archived',
          'moduleId', 'moduleIds', 'conversationIds', 'conversationLinks',
          'version', 'visibility', 'tags', 'attributes',
          'etag', '_etag',
          // Outbound-sync bookkeeping (Xavior→Google) — internal, never user-facing.
          'pushStatus', 'idempotencyToken', 'externalId', 'providerSyncedAt',
          'providerUpdatedAt', 'providerIntegrationId', 'localDirtyAt',
          'mergedDuplicate', 'orphanNoModule',
          'shopifyOrders', 'shopifyTotalSpent', 'shopifyOrderCount',
          'shopifyFirstOrderDate', 'shopifyLastOrderDate', 'shopifyStores',
          // Internal flags from save round-trips
          '_stayOpen', '_forceCreate', '_existingContactId', '_existingEtag',
          // Internal: per-contact prep note for the meeting-prep tool.
          // Surfaced to the agent at briefing time, never to the user
          // viewing the contact card.
          'prepNote', 'prep_note',
          // Onboarding/email discovery + extraction bookkeeping —
          // provenance tag, auto-inferred personal/work class, raw
          // email-stat counters, and the enrichment state machine.
          // Internal plumbing written by contact-gen.ts /
          // onboarding.router.ts, never user-facing on the card.
          'source', 'relationship',
          'emailCount', 'sentCount', 'receivedCount',
          'enrichmentStatus', 'enriched', 'twoWay', 'aiProfile',
        ]);
        const humanizeKey = (k: string) => k
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/_/g, ' ')
          .replace(/^./, (c) => c.toUpperCase());
        const formatValue = (v: unknown): string => {
          if (v == null) return '';
          if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ');
          if (typeof v === 'string') {
            // Full ISO date → "July 5, 1962"
            if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
              const d = new Date(`${v}T00:00:00`);
              return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
            }
            // Recurring-birthday shape (--MM-DD) emitted by
            // sanitizeBirthDate when only month/day are known →
            // "July 5".
            const noYear = /^--(\d{2})-(\d{2})$/.exec(v);
            if (noYear) {
              const d = new Date(`2000-${noYear[1]}-${noYear[2]}T00:00:00`);
              return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
            }
          }
          if (typeof v === 'object') return '';
          return String(v);
        };
        // Case- and separator-insensitive match so firstName /
        // first_name / FIRSTNAME / first-name all hit the same
        // hidden entry. API contracts duplicate fields in both
        // camelCase and snake_case shapes on contacts.
        const normalize = (s: string) => s.toLowerCase().replace(/[_-]/g, '');
        const HIDDEN_LC = new Set(Array.from(HIDDEN).map(normalize));
        const entries = Object.entries(attrs).filter(([k, v]) => {
          if (HIDDEN_LC.has(normalize(k))) return false;
          // Internal / system fields commonly prefixed with underscore.
          if (k.startsWith('_')) return false;
          if (v == null) return false;
          // Drop booleans — never useful as a fact row.
          if (typeof v === 'boolean') return false;
          if (typeof v === 'object' && !Array.isArray(v)) return false;
          if (Array.isArray(v) && v.length === 0) return false;
          if (typeof v === 'string' && !v.trim()) return false;
          // ISO timestamps (full datetime with T/Z) are system signal,
          // not user-stated facts.
          if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) return false;
          return true;
        });
        if (entries.length === 0) return null;
        // No "More" heading — facts flow inline as additional rows
        // alongside Email / Phone / etc. in the Contact Details area.
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', rowGap: 8, columnGap: 12, fontSize: 13, marginTop: 8, marginBottom: 20 }}>
            {entries.map(([k, v]) => (
              <React.Fragment key={k}>
                <span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>{humanizeKey(k)}</span>
                <span style={{ color: 'var(--color-text)' }}>{formatValue(v)}</span>
              </React.Fragment>
            ))}
          </div>
        );
      })()}

      {/* Mobile activity button */}
      {isMobile && (
        <div style={{ marginBottom: 20 }}>
          <button
            type="button"
            onClick={() => setShowMobileActivity(true)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '14px 0', borderRadius: 12, border: 'none',
              background: 'var(--xavior-blue-bg, #e0f2fe)', fontSize: 15, fontWeight: 600, color: 'var(--color-brand)', cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
            Activity
          </button>
        </div>
      )}

      {/* Total Spent */}
      {(() => {
        const attrs = contact?.attributes || {};
        const totalSpent = contact?.shopifyTotalSpent ?? attrs.shopifyTotalSpent;
        if (!totalSpent || totalSpent <= 0) return null;
        return (
          <div style={{ padding: '10px 14px', background: 'var(--xavior-blue-bg, #e0f2fe)', borderRadius: 8, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-brand)' }}>Total Spent</span>
            <span style={{ fontSize: 17, fontWeight: 700, color: 'var(--color-text)' }}>
              ${totalSpent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        );
      })()}

      {/* Deal Value and Probability */}
      {(showDealValue || showProbability) && (dealValue || probability) && (
        <div style={{ display: 'flex', gap: 16, fontSize: 13, marginBottom: 16 }}>
          {showDealValue && dealValue && (
            <div><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Value: </span><span style={{ color: 'var(--color-text)' }}>${dealValue}</span></div>
          )}
          {showProbability && probability && (
            <div><span style={{ color: 'var(--color-text-muted)', fontWeight: 500 }}>Probability: </span><span style={{ color: 'var(--color-text)' }}>{probability}%</span></div>
          )}
        </div>
      )}

      {/* Low-confidence acknowledgment banner — shown when AI returned
          a low-confidence match and the user hasn't confirmed yet.
          Once the user confirms, enrichmentReviewedAt is set and the
          banner (and the kanban badge) disappear. Bulk auto-enrich also
          skips contacts with enrichmentReviewedAt set. */}
      {enrichmentConfidence === 'low' && !enrichmentReviewedAtLocal && !(contact?.enrichmentReviewedAt || (contact?.attributes as Record<string, unknown> | undefined)?.enrichmentReviewedAt) && (
        <div style={{
          marginBottom: 16, padding: '12px 14px',
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }} aria-hidden="true">⚠️</span>
            <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.5 }}>
              <strong>AI was uncertain about this match.</strong>{' '}
              Review the profile below. If this looks right, confirm — or add a LinkedIn URL or bio to help AI identify the correct person.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                if (!contact) return;
                const reviewedAt = new Date().toISOString();
                // Optimistically hide the banner so the click has
                // immediate visible effect even though the parent's
                // _stayOpen save path doesn't refetch the contact.
                setEnrichmentReviewedAtLocal(reviewedAt);
                // Send the field at the top level only — the backend
                // PATCH destructure stores top-level body keys as
                // attributes; a nested `attributes: nextAttrs` would
                // double-nest (#2186).
                onSave({
                  enrichmentReviewedAt: reviewedAt,
                  _stayOpen: true,
                } as Partial<ContactObject> & { _stayOpen?: boolean });
              }}
              style={{
                padding: '7px 14px', borderRadius: 6,
                background: '#c4841d', color: 'white', border: 'none',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              ✓ Confirm identity
            </button>
            <span style={{ fontSize: 11, color: '#a16207', alignSelf: 'center', lineHeight: 1.4 }}>
              Confirmed contacts won't be re-flagged by auto-enrich.
            </span>
          </div>
        </div>
      )}

      {/* Profile / bio — single section that prefers AI-generated
          enrichmentSummary, falls back to the shorter summary
          field (CSV import or user-entered). Gate on having a
          body to render: a contact with only `enrichmentConfidence`
          set (e.g. a 'none' attempt) used to render the header +
          Remove AI button with an empty body. */}
      {(enrichmentSummary || summary) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Profile</span>
            <button
              type="button"
              onClick={() => {
                if (!contact) return;
                const ok = window.confirm(
                  'Remove AI enhancements?\n\n' +
                  'This will clear the bio, summary, skills, title, location, company, phone, LinkedIn URL, and X handle — anything AI may have populated.\n\n' +
                  'Your name, email, and notes will not be touched.\n\n' +
                  'The contact will be excluded from future automatic enrichment. You can still click the AI button to re-enrich manually.'
                );
                if (!ok) return;
                setEnrichmentSummary('');
                setEnrichmentSkills([]);
                setEnrichmentConfidence('');
                setEnrichmentAttemptedAt('');
                setContactTitle('');
                setLocation('');
                setLinkedinUrl('');
                setFirm('');
                setCompany('');
                setSummary('');
                setXHandle('');
                setPhone('');
                // Send all fields at the top level — the backend's
                // PATCH handler (apps/api/src/routes/objects.ts:968)
                // destructures top-level body keys into the stored
                // attributes. A nested `attributes: nextAttrs` would
                // end up double-nested at `attributes.attributes.*`
                // and the read-side check at line 1525 of this file
                // (`contact.attributes.doNotBulkEnrich`) would never
                // see the opt-out flag, causing auto-enrichment to
                // re-populate the data the user just removed.
                onSave({
                  enrichmentSummary: '',
                  enrichmentSkills: [],
                  enrichmentConfidence: '',
                  enrichmentAttemptedAt: '',
                  // Clear reviewedAt too so the badge state is fully
                  // reset; otherwise a previously-skipped low-conf
                  // attempt would keep its "reviewed" sticker even
                  // though all the AI fields are gone.
                  enrichmentReviewedAt: '',
                  contactTitle: '',
                  location: '',
                  linkedinUrl: '',
                  firm: '',
                  organization: '',
                  summary: '',
                  xHandle: '',
                  // Phone may have been written from email signature
                  // extraction or low-conf enrichment fallback. Drop
                  // both top-level and attributes-stored shapes.
                  phone: '',
                  phones: [],
                  doNotBulkEnrich: true,
                } as Partial<ContactObject>);
              }}
              title="Remove all AI-populated data on this contact"
              aria-label="Remove AI data"
              onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: 'transparent', border: 'none', borderRadius: 6,
                padding: '4px 8px', cursor: 'pointer', color: 'var(--color-text-faint)',
                fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              Remove AI data
            </button>
          </div>
          {(enrichmentSummary || summary) && (
            <div style={{
              background: '#F8F9FA', border: '1px solid #DADCE0', borderRadius: 8,
              padding: '12px 14px', fontSize: 13, color: 'var(--color-text)', lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}>
              {enrichmentSummary || summary}
            </div>
          )}
        </div>
      )}

      {/* Skills/tags — hidden on mobile to save space */}
      {!isMobile && (enrichmentSkills.length > 0 || enrichmentTags.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {enrichmentSkills.map((skill, i) => (
            <span key={`skill-${i}`} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 12,
              background: 'var(--xavior-blue-bg, #e0f2fe)', color: 'var(--color-brand)', fontWeight: 500,
            }}>{skill}</span>
          ))}
          {enrichmentTags.map((tag, i) => (
            <span key={`tag-${i}`} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 12,
              background: '#eef3fb', color: '#3b6fc0', fontWeight: 500,
            }}>{tag}</span>
          ))}
        </div>
      )}

      {/* Hints prompt — appears when AI returns low/no confidence */}
      {enrichHintsPrompt && (
        <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fef7ec', border: '1px solid #fde68a', borderRadius: 8 }}>
          <div style={{ fontSize: 12, color: '#92400e', marginBottom: 8 }}>
            AI couldn't find a strong match. Add keywords to help (e.g. company, city, industry):
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={enrichHints}
              onChange={(e) => setEnrichHints(e.target.value)}
              placeholder="e.g. bankers trust salomon NYC"
              onKeyDown={(e) => { if (e.key === 'Enter' && enrichHints.trim()) handleEnrich(enrichHints); }}
              style={{
                flex: 1, padding: '7px 10px', borderRadius: 6,
                border: '1px solid #fde68a', fontSize: 13, outline: 'none',
                fontFamily: 'inherit', background: 'var(--color-surface)',
              }}
              autoFocus
            />
            <button
              type="button"
              onClick={() => { if (enrichHints.trim()) handleEnrich(enrichHints); }}
              style={{
                padding: '7px 14px', borderRadius: 6,
                background: '#c4841d', color: 'white', border: 'none',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
              }}
            >
              Retry
            </button>
            <button
              type="button"
              onClick={() => {
                if (!contact) return;
                setEnrichHintsPrompt(false);
                const reviewedAt = new Date().toISOString();
                setEnrichmentAttemptedAt(reviewedAt);
                // Top-level fields only — see comment on Remove AI
                // data above for why nested `attributes` was wrong.
                onSave({
                  enrichmentReviewedAt: reviewedAt,
                  doNotBulkEnrich: true,
                } as Partial<ContactObject>);
              }}
              title="Mark this contact reviewed and exclude it from bulk enrichment. The AI button still works manually."
              style={{
                padding: '7px 14px', borderRadius: 6,
                background: 'var(--color-surface)', color: '#92400e', border: '1px solid #fde68a',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
              }}
            >
              Skip AI
            </button>
          </div>
        </div>
      )}

      {/* Save / Wrong Person buttons — appear after enrichment until saved */}
      {enrichUnsaved && !isNew && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button
            type="button"
            onClick={handleSaveEnrichment}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 8,
              background: 'var(--color-brand)', color: 'white', border: 'none',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Save Profile
          </button>
          <button
            type="button"
            onClick={() => {
              // Clear ALL enrichment data and auto-filled fields — keep only name
              setEnrichmentSummary('');
              setEnrichmentSkills([]);
              setEnrichmentConfidence('');
              setEnrichResult(null);
              setEnrichUnsaved(false);
              setEnrichHintsPrompt(true);
              setEnrichHints('');
              // Clear all fields that may have been auto-filled with wrong data
              setCompany('');
              setContactTitle('');
              setLocation('');
              setLinkedinUrl('');
              setSummary('');
              setFirm('');
            }}
            style={{
              flex: 1, padding: '10px 16px', borderRadius: 8,
              background: 'var(--color-surface)', color: '#dc2626', border: '1px solid #fecaca',
              fontSize: 14, fontWeight: 500, cursor: 'pointer',
            }}
          >
            Wrong Person
          </button>
        </div>
      )}

      {/* Label */}
      {label && (
        <div style={{ marginBottom: 16, paddingTop: 12, borderTop: '1px solid #f0ede8' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-muted)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 6 }}>Label</div>
          <span style={{ fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 12, backgroundColor: labelColor, color: '#1f2937' }}>{label}</span>
        </div>
      )}

      {/* Shopify Purchase History */}
      {(() => {
        const attrs = contact?.attributes || {};
        const shopifyOrders = contact?.shopifyOrders || attrs.shopifyOrders || [];
        const shopifyTotalSpent = contact?.shopifyTotalSpent ?? attrs.shopifyTotalSpent;
        const shopifyOrderCount = contact?.shopifyOrderCount ?? attrs.shopifyOrderCount;
        const shopifyStores = contact?.shopifyStores || attrs.shopifyStores || [];
        const shopifyFirstOrderDate = contact?.shopifyFirstOrderDate || attrs.shopifyFirstOrderDate;
        const shopifyLastOrderDate = contact?.shopifyLastOrderDate || attrs.shopifyLastOrderDate;
        if (!shopifyOrders || shopifyOrders.length === 0) return null;
        return (
          <div style={{ padding: '10px 14px', background: '#f3f0fa', border: '1px solid #DADCE0', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: '#7c5cbf', marginBottom: 8 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              Purchase History
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 13 }}>
              <div><span style={{ color: '#7c5cbf' }}>Total:</span> <span style={{ fontWeight: 600 }}>${shopifyTotalSpent?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</span></div>
              <div><span style={{ color: '#7c5cbf' }}>Orders:</span> {shopifyOrderCount || shopifyOrders.length}</div>
              {shopifyStores.length > 0 && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#7c5cbf' }}>Stores:</span> {shopifyStores.join(', ')}</div>}
              {shopifyFirstOrderDate && <div><span style={{ color: '#7c5cbf' }}>Since:</span> {new Date(shopifyFirstOrderDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>}
              {shopifyLastOrderDate && <div><span style={{ color: '#7c5cbf' }}>Last:</span> {new Date(shopifyLastOrderDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
            </div>
            <details style={{ marginTop: 8 }}>
              <summary style={{ fontSize: 12, color: '#7c5cbf', cursor: 'pointer' }}>View {shopifyOrders.length} order{shopifyOrders.length !== 1 ? 's' : ''}</summary>
              <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto' }}>
                {shopifyOrders.slice(0, 10).map((order: ShopifyOrderSummary, idx: number) => (
                  <div key={idx} style={{ padding: '6px 8px', background: 'var(--color-surface)', borderRadius: 6, border: '1px solid #DADCE0', marginBottom: 4, fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500 }}>
                      <span>{order.orderNumber}</span>
                      <span>${order.total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    {order.date && <div style={{ color: '#7c5cbf', marginTop: 2 }}>{new Date(order.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}{order.store && ` · ${order.store}`}</div>}
                  </div>
                ))}
              </div>
            </details>
          </div>
        );
      })()}

    </div>
  );
}
